import { useSelector } from "react-redux";
import {
  selectFundScheduleSelectModels,
  selectLastUpdateTime,
  getFundScheduleSelectModels,
  selectLoading,
  selectServiceChargeOnlyModels,
  selectServiceChargeLastUpdatedTime,
} from "./components/FundScheduleSelectSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import useCaching, { CachingOptions } from "cpl-react-common/Hooks/useCaching";
import MultiSelectDropdown from "cpl-react-common/components/Inputs/MultiSelectDropdown";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useCallback, useEffect } from "react";

type FundScheduleSelectProps = {
  "data-test-id"?: string;
  handleChange: (selections: number[]) => void;
  defaultAll: boolean;
  defaultSome?: number[];
  disabled?: boolean;
  developmentIDs?: string[];
  isServiceChargeOnly?: boolean;
};

const EXPIRY_PERIOD_MS = 1000 * 60 * 30; // 30 minutes

const FundScheduleSelectMulti: React.FC<FundScheduleSelectProps> = (props) => {
  const loading = useSelector(selectLoading);
  const fundScheduleSelectModels = useSelector(
    props.isServiceChargeOnly ? selectServiceChargeOnlyModels : selectFundScheduleSelectModels
  );
  const lastUpdateTime = useSelector(
    props.isServiceChargeOnly ? selectServiceChargeLastUpdatedTime : selectLastUpdateTime
  );
  const thunkDispatch = useThunkDispatch();

  const updateSelectModels = useCallback(() => {
    thunkDispatch(
      getFundScheduleSelectModels({
        isServiceChargeOnly: props.isServiceChargeOnly,
        developmentIDs: props.developmentIDs,
      })
    );
  }, [props.isServiceChargeOnly, props.developmentIDs, thunkDispatch]);

  const cachingOptions: CachingOptions = {
    lastUpdateTime,
    expiryPeriodMs: EXPIRY_PERIOD_MS,
    updateData: updateSelectModels,
    shouldUpdateOnLoad: fundScheduleSelectModels.length === 0,
  };

  useCaching(cachingOptions);

  useEffect(() => {
    updateSelectModels();
  }, [props.developmentIDs, props.isServiceChargeOnly, updateSelectModels]);

  return (
    <ActivityIndicator isBusy={loading}>
      <MultiSelectDropdown
        data-test-id={props["data-test-id"]}
        handleChange={(selections) => props.handleChange(selections as number[])}
        options={fundScheduleSelectModels.map((x) => ({
          value: x.value!,
          label: x.label ?? "",
        }))}
        defaultAll={props.defaultAll}
        defaultSome={props.defaultSome}
        searchable={true}
        disabled={props.disabled}
      ></MultiSelectDropdown>
    </ActivityIndicator>
  );
};

export default FundScheduleSelectMulti;
